import React, { useState, useEffect } from 'react';

export default function Community() {
  // Fetch from localStorage
  const apiBase = localStorage.getItem('apiBase') || ' https://sadaatagrofarms.com/api';
  const userId = localStorage.getItem('userId') || '';
  const authToken = localStorage.getItem('authToken') || '';

  // State management
  const [status, setStatus] = useState({ message: '', isOk: true });
  const [posts, setPosts] = useState([]);
  const [postForm, setPostForm] = useState({
    text: '',
    image: null,
    video: null,
    voice: null
  });
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [comments, setComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [animatingLikes, setAnimatingLikes] = useState({});

  useEffect(() => {
    loadFeed();
  }, []);

  // Helper functions
  const updateStatus = (message, isOk = true) => {
    setStatus({ message, isOk });
  };

  // Centralized fetch with auth token
  const fetchJSON = async (url, options = {}) => {
    if (!authToken) {
      throw new Error('Authentication token not found. Please log in.');
    }

    options.headers = {
      ...(options.headers || {}),
      Authorization: `Bearer ${authToken}`,
    };

    const res = await fetch(url, options);
    const text = await res.text();
    let data = null;
    try {
      data = JSON.parse(text);
    } catch (e) {
      data = text;
    }

    if (!res.ok) {
      throw new Error(typeof data === 'string' ? data : (data?.detail || JSON.stringify(data)));
    }
    return data;
  };

  const safeText = (text) => (text ?? '').toString()
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');

  // Load feed
  const loadFeed = async () => {
    try {
      updateStatus('Loading feed...', true);
      const data = await fetchJSON(`${apiBase.replace(/\/+$/, '')}/community/posts/`);
      setPosts(data);
      updateStatus('Feed loaded ✅', true);
    } catch (err) {
      updateStatus('Error: ' + err.message, false);
    }
  };

  // Create post
  const createPost = async () => {
    if (!userId) {
      updateStatus('User ID not found.', false);
      return;
    }

    const fd = new FormData();
    fd.append('user_id', userId);
    fd.append('text', postForm.text || '');
    if (postForm.image) fd.append('image', postForm.image);
    if (postForm.video) fd.append('video', postForm.video);
    if (postForm.voice) fd.append('voice', postForm.voice);

    try {
      updateStatus('Posting...', true);
      await fetchJSON(`${apiBase.replace(/\/+$/, '')}/community/posts/`, {
        method: 'POST',
        body: fd
      });
      updateStatus('Posted ✅', true);
      clearPostForm();
      await loadFeed();
    } catch (err) {
      updateStatus('Error: ' + err.message, false);
    }
  };

  const clearPostForm = () => {
    setPostForm({ text: '', image: null, video: null, voice: null });
  };

  // Toggle post like
  const togglePostLike = async (postId) => {
    if (!userId) {
      updateStatus('User ID not found.', false);
      return;
    }

    const fd = new FormData();
    fd.append('user_id', userId);

    try {
      // Trigger animation
      setAnimatingLikes(prev => ({ ...prev, [`post-${postId}`]: true }));
      
      updateStatus('Toggling like...', true);
      await fetchJSON(`${apiBase.replace(/\/+$/, '')}/community/posts/${postId}/like/`, {
        method: 'POST',
        body: fd
      });
      updateStatus('Done ✅', true);
      await loadFeed();
      
      // Remove animation after delay
      setTimeout(() => {
        setAnimatingLikes(prev => ({ ...prev, [`post-${postId}`]: false }));
      }, 600);
    } catch (err) {
      updateStatus('Error: ' + err.message, false);
      setAnimatingLikes(prev => ({ ...prev, [`post-${postId}`]: false }));
    }
  };

  // Comments
  const toggleComments = async (postId) => {
    if (expandedPostId === postId) {
      // Close comments
      setExpandedPostId(null);
    } else {
      // Open comments
      setExpandedPostId(postId);
      await loadComments(postId);
    }
  };

  const loadComments = async (postId) => {
    try {
      const data = await fetchJSON(`${apiBase.replace(/\/+$/, '')}/community/posts/${postId}/comments/`);
      setComments(prev => ({ ...prev, [postId]: data }));
    } catch (err) {
      updateStatus('Error loading comments: ' + err.message, false);
    }
  };

  const addComment = async (postId) => {
    if (!userId) {
      updateStatus('User ID not found.', false);
      return;
    }
    const text = commentText[postId] || '';
    if (!text.trim()) return;

    const fd = new FormData();
    fd.append('user_id', userId);
    fd.append('text', text.trim());

    try {
      await fetchJSON(`${apiBase.replace(/\/+$/, '')}/community/posts/${postId}/comments/`, {
        method: 'POST',
        body: fd
      });
      setCommentText(prev => ({ ...prev, [postId]: '' }));
      await loadComments(postId);
      await loadFeed();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const toggleCommentLike = async (commentId, postId) => {
    if (!userId) {
      updateStatus('User ID not found.', false);
      return;
    }

    const fd = new FormData();
    fd.append('user_id', userId);

    try {
      // Trigger animation
      setAnimatingLikes(prev => ({ ...prev, [`comment-${commentId}`]: true }));
      
      await fetchJSON(`${apiBase.replace(/\/+$/, '')}/community/comments/${commentId}/like/`, {
        method: 'POST',
        body: fd
      });
      await loadComments(postId);
      await loadFeed();
      
      // Remove animation after delay
      setTimeout(() => {
        setAnimatingLikes(prev => ({ ...prev, [`comment-${commentId}`]: false }));
      }, 600);
    } catch (err) {
      alert('Error: ' + err.message);
      setAnimatingLikes(prev => ({ ...prev, [`comment-${commentId}`]: false }));
    }
  };

  // Helper to ensure media URLs use https
  const secureUrl = (url) => url ? url.replace('http://', 'https://') : url;

  return (
    <div style={styles.wrap}>
      {status.message && (
        <div style={{ ...styles.status, color: status.isOk ? '#76ffb2' : '#ff8a8a' }}>
          {status.message}
        </div>
      )}

      {/* Create Post */}
      <div style={styles.card}>
        <h3 style={{ margin: '0 0 8px 0' }}>Create Post</h3>
        <div style={styles.muted}>Send text + optional image/video/voice (max 20MB)</div>
        <div style={{ marginTop: '10px' }}>
          <textarea
            style={styles.textarea}
            placeholder="Write something..."
            value={postForm.text}
            onChange={(e) => setPostForm({ ...postForm, text: e.target.value })}
          />
        </div>

        <div style={{ ...styles.row, marginTop: '10px' }}>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <div style={styles.muted}>Image</div>
            <input
              style={styles.fileInput}
              type="file"
              accept="image/*"
              onChange={(e) => setPostForm({ ...postForm, image: e.target.files[0] })}
            />
          </div>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <div style={styles.muted}>Video</div>
            <input
              style={styles.fileInput}
              type="file"
              accept="video/*"
              onChange={(e) => setPostForm({ ...postForm, video: e.target.files[0] })}
            />
          </div>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <div style={styles.muted}>Voice</div>
            <input
              style={styles.fileInput}
              type="file"
              accept="audio/*"
              onChange={(e) => setPostForm({ ...postForm, voice: e.target.files[0] })}
            />
          </div>
        </div>

        <div style={styles.actions}>
          <button style={{ ...styles.button, ...styles.pill }} onClick={createPost}>
            Post
          </button>
          <button style={{ ...styles.button, ...styles.pill }} onClick={clearPostForm}>
            Clear
          </button>
        </div>
      </div>

      {/* Feed */}
      <div style={styles.card}>
        <div style={{ ...styles.row, justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Feed</h3>
          <div style={styles.muted}>{posts.length} posts</div>
        </div>

        {posts.map((post) => (
          <div key={post.id} style={{ ...styles.card, margin: '12px 0' }}>
            <div style={styles.postHead}>
              <div>
                <div style={{ fontWeight: 700 }}>Post #{post.id}</div>
                <div style={styles.postMeta}>
                  Posted by {post.username} • {post.created_at}
                </div>
              </div>
              <div style={styles.muted}>
                ❤️ {post.likes_count} • 💬 {post.comments_count}
              </div>
            </div>

            {post.text && (
              <div style={{ marginTop: '10px', whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: safeText(post.text) }} />
            )}

            {/* Media */}
            <div style={styles.media}>
              {post.image && <img src={secureUrl(post.image)} alt="post" style={styles.mediaItem} />}
              {post.video && <video controls src={secureUrl(post.video)} style={styles.mediaItem} />}
              {post.voice && <audio controls src={secureUrl(post.voice)} style={styles.mediaItem} />}
            </div>

            <div style={styles.actions}>
              <button
                style={{ 
                  ...styles.button, 
                  ...styles.pill,
                  ...(animatingLikes[`post-${post.id}`] ? styles.likeAnimating : {})
                }}
                onClick={() => togglePostLike(post.id)}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#6b7280', fontSize: '18px' }}>❤️</span>
                  {post.user_has_liked ? 'Unlike' : 'Like'}
                </span>
              </button>
              <button 
                style={{ ...styles.button, ...styles.pill }} 
                onClick={() => toggleComments(post.id)}
              >
                {expandedPostId === post.id ? 'Hide Comments' : 'Show Comments'}
              </button>
            </div>

            {/* Comments Section - Inline under each post */}
            {expandedPostId === post.id && (
              <div style={styles.commentsSection}>
                <div style={styles.commentsSectionHeader}>
                  <div style={{ fontWeight: 700, fontSize: '16px' }}>Comments</div>
                  <div style={styles.muted}>Post #{post.id}</div>
                </div>

                <div style={{ ...styles.row, marginBottom: '16px', marginTop: '12px' }}>
                  <input
                    style={{ ...styles.input, flex: 1, minWidth: '240px' }}
                    placeholder="Write a comment..."
                    value={commentText[post.id] || ''}
                    onChange={(e) => setCommentText(prev => ({ ...prev, [post.id]: e.target.value }))}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        addComment(post.id);
                      }
                    }}
                  />
                  <button 
                    style={{ ...styles.button, ...styles.pill, ...styles.pillPrimary }} 
                    onClick={() => addComment(post.id)}
                  >
                    Send
                  </button>
                </div>

                <div>
                  {!comments[post.id] || comments[post.id].length === 0 ? (
                    <div style={styles.muted}>No comments yet. Be the first to comment!</div>
                  ) : (
                    comments[post.id].map((comment) => (
                      <div key={comment.id} style={styles.comment}>
                        <div style={styles.commentRow}>
                          <div style={{ flex: 1 }}>
                            <div>
                              <b>Comment #{comment.id}</b>{' '}
                              <span style={styles.muted}>
                                by {comment.username} (ID: {comment.user_id})
                              </span>
                            </div>
                            <div style={{ whiteSpace: 'pre-wrap', marginTop: '6px' }} dangerouslySetInnerHTML={{ __html: safeText(comment.text) }} />
                            <div style={{ ...styles.muted, marginTop: '6px' }}>
                              ❤️ {comment.likes_count} • {comment.created_at}
                            </div>
                          </div>
                          <div>
                            <button
                              style={{ 
                                ...styles.button, 
                                ...styles.smallBtn, 
                                ...styles.pillPrimary,
                                ...(animatingLikes[`comment-${comment.id}`] ? styles.likeAnimating : {})
                              }}
                              onClick={() => toggleCommentLike(comment.id, post.id)}
                            >
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ color: '#6b7280', fontSize: '16px' }}>❤️</span>
                                Like
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Styles object
const styles = {
  wrap: {
    maxWidth: "1000px",
    margin: "0 auto",
    padding: "20px",
    fontFamily: "Segoe UI, Arial, sans-serif",
    color: "#1f2937",
    minHeight: "50vh",
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    boxShadow: "0 8px 32px rgba(31, 38, 135, 0.15)",
    border: "1px solid rgba(255, 255, 255, 0.25)",
    borderRadius: "20px",
  },

  card: {
    background: "rgba(255, 255, 255, 0.12)",
    backdropFilter: "blur(15px) saturate(150%)",
    WebkitBackdropFilter: "blur(15px) saturate(150%)",
    border: "1px solid rgba(255, 255, 255, 0.25)",
    borderRadius: "16px",
    padding: "20px",
    margin: "15px 0",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
    transition: "all 0.3s ease",
  },

  row: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },

  input: {
    borderRadius: "10px",
    border: "2px solid rgba(59, 130, 246, 0.3)",
    background: "rgba(255, 255, 255, 0.2)",
    color: "#1f2937",
    padding: "10px 14px",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    outline: "none",
    transition: "all 0.3s ease",
    fontSize: "14px",
  },

  textarea: {
    borderRadius: "10px",
    border: "2px solid rgba(59, 130, 246, 0.3)",
    background: "rgba(255, 255, 255, 0.2)",
    color: "#1f2937",
    padding: "12px 14px",
    minHeight: "80px",
    width: "100%",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    fontFamily: "inherit",
    outline: "none",
    transition: "all 0.3s ease",
    fontSize: "14px",
    resize: "vertical",
  },

  fileInput: {
    borderRadius: "10px",
    border: "2px solid rgba(59, 130, 246, 0.3)",
    background: "rgba(255, 255, 255, 0.2)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    padding: "10px",
    width: "100%",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },

  button: {
    borderRadius: "10px",
    border: "2px solid rgba(59, 130, 246, 0.4)",
    background: "rgba(59, 130, 246, 0.2)",
    color: "#1e40af",
    padding: "10px 20px",
    cursor: "pointer",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    fontWeight: "600",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
  },

  muted: {
    color: "#6b7280",
    fontSize: "13px",
    fontWeight: "500",
  },

  postHead: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "flex-start",
  },

  postMeta: {
    fontSize: "12px",
    color: "#6b7280",
    fontWeight: "500",
  },

  actions: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    marginTop: "10px",
  },

  pill: {
    padding: "8px 16px",
    borderRadius: "999px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  },

  pillPrimary: {
    background: "rgba(59, 130, 246, 0.2)",
    border: "1px solid rgba(59, 130, 246, 0.4)",
    color: "#1e40af",
  },

  media: {
    marginTop: "10px",
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  },

  mediaItem: {
    maxWidth: "100%",
    borderRadius: "14px",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },

  // Comments Section - Inline under post
  commentsSection: {
    marginTop: "16px",
    padding: "16px",
    background: "rgba(59, 130, 246, 0.05)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: "12px",
    border: "1px solid rgba(59, 130, 246, 0.2)",
  },

  commentsSectionHeader: {
    paddingBottom: "12px",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    marginBottom: "12px",
  },

  comment: {
    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
    padding: "16px 0",
    background: "rgba(255, 255, 255, 0.05)",
    marginTop: "8px",
    borderRadius: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
  },

  commentRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
    alignItems: "flex-start",
  },

  smallBtn: {
    padding: "6px 12px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: "1px solid",
  },

  status: {
    marginTop: "12px",
    padding: "12px 16px",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "500",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
  },

  likeAnimating: {
    animation: "likeAnimation 0.6s ease",
    transform: "scale(1.1)",
  },
};

export { styles };

// Add this CSS to your global styles or inject it in your component
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = `
    @keyframes likeAnimation {
      0% {
        transform: scale(1);
      }
      25% {
        transform: scale(1.2);
      }
      50% {
        transform: scale(0.95);
      }
      75% {
        transform: scale(1.1);
      }
      100% {
        transform: scale(1);
      }
    }

    @keyframes heartBeat {
      0%, 100% {
        transform: scale(1);
      }
      10%, 30% {
        transform: scale(1.3);
      }
      20%, 40% {
        transform: scale(1.1);
      }
    }
  `;
  if (!document.getElementById('like-animations')) {
    styleSheet.id = 'like-animations';
    document.head.appendChild(styleSheet);
  }
}